import { PlusIcon, TrashIcon, XIcon } from '@primer/octicons-react';
import { ActionList, AnchoredOverlay, Box, IconButton, Link, Spinner } from '@primer/react';
import { useState } from 'react';
import { uploadApi } from '../../../../api/uploadApi';
import styles from "./ThucDonFiles.module.css";

interface IThucDonFilesProps {
    files: string[];
    isReadOnly?: boolean;
    onFilesChanged: (data: string[]) => void;
}

interface IThucDonImagePreviewProps {
    imgFile: string;
}

const ThucDonImagePreview = ({ imgFile }: IThucDonImagePreviewProps) => {
    const [open, setOpen] = useState(false);
    return (
        <AnchoredOverlay
            open={open}
            onClose={() => setOpen(false)}
            overlayProps={{ role: 'dialog', 'aria-modal': true, 'aria-label': 'Ảnh thực đơn' }}
            focusZoneSettings={{ disabled: true }}
            renderAnchor={(anchorProps) => (
                <Box
                    {...anchorProps}
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}
                    style={{
                        backgroundImage: `url("${imgFile}")`,
                        position: "relative"
                    }}
                    className={styles.imgPreview}
                    sx={{
                        borderStyle: "solid",
                        borderWidth: 1,
                        borderRadius: 2,
                        borderColor: "border.default",
                        cursor: "pointer"
                    }}
                />
            )}
        >
            <Box
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <img
                    src={imgFile}
                    alt="Ảnh thực đơn"
                    style={{
                        width: '300px',
                        height: '300px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        display: 'block',
                    }}
                />
            </Box>
        </AnchoredOverlay>
    );
};

export const isImgFile = (fileName: string) => {
    if (fileName) {
        const fileType = fileName.split('.').pop()?.toLowerCase();
        return (
            fileType === "png"
            || fileType === "svg"
            || fileType === "jpg"
            || fileType === "gif"
            || fileType === "jpeg"
        );
    }
    return false;
};

const ThucDonFiles = (props: IThucDonFilesProps) => {
    const [isUploading, setIsUploading] = useState(false);

    const handleSelectFile = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('multiple', 'true');
        input.setAttribute('accept', 'image/png, image/gif, image/jpeg');
        input.click();
        input.onchange = async () => {
            if (input.files) {
                setIsUploading(true);
                const res = await uploadApi.uploadImages(input.files);
                setIsUploading(false);
                if (res.is_success && res.data) {
                    const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
                    if (Array.isArray(list) && list.length > 0) {
                        props.onFilesChanged([
                            ...props.files,
                            ...list.map((x: any) => typeof x === 'string' ? x : x.url)
                        ]);
                    }
                }
            }
        };
    };

    return (
        <Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "flex-start" }}>
                {props.files.filter(x => isImgFile(x)).map((imgFile, idx) => {
                    if (props.isReadOnly === true) {
                        return <ThucDonImagePreview key={idx} imgFile={imgFile} />;
                    }
                    return (
                        <Box
                            key={idx}
                            style={{ backgroundImage: `url("${imgFile}")` }}
                            className={styles.imgPreview}
                            sx={{
                                borderStyle: "solid",
                                borderWidth: 1,
                                borderColor: "border.default"
                            }}
                        >
                            <Box
                                className={styles.xButton}
                                onClick={() => props.onFilesChanged(props.files.filter(x => x !== imgFile))}
                                sx={{
                                    bg: "danger.emphasis",
                                    color: "fg.onEmphasis",
                                    '&:hover': { bg: "danger.fg" }
                                }}
                            >
                                <XIcon size={10} />
                            </Box>
                        </Box>
                    );
                })}

                {props.isReadOnly !== true && (
                    <Box
                        onClick={isUploading ? undefined : handleSelectFile}
                        sx={{
                            width: "54px",
                            height: "54px",
                            border: "2px dashed",
                            borderColor: "border.default",
                            borderRadius: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "2px",
                            cursor: isUploading ? "wait" : "pointer",
                            color: "fg.muted",
                            transition: "all 0.15s",
                            '&:hover': {
                                borderColor: "accent.fg",
                                color: "accent.fg",
                                bg: "accent.subtle"
                            }
                        }}
                    >
                        {isUploading ? (
                            <Spinner size="small" />
                        ) : (
                            <>
                                <PlusIcon size={16} />
                                <Box sx={{ fontSize: "9px", fontWeight: 600, letterSpacing: "0.02em" }}>Tải ảnh</Box>
                            </>
                        )}
                    </Box>
                )}
            </Box>

            <ActionList showDividers>
                {props.files.filter(x => !isImgFile(x)).map((file, idx) => {
                    return (
                        <ActionList.LinkItem key={idx} sx={{ display: "flex" }}>
                            <Link href={file} target="_blank" sx={{ display: "flex" }}>
                                <ActionList.LeadingVisual />
                                {file}
                            </Link>
                            <ActionList.TrailingVisual>
                                <IconButton
                                    sx={{ mt: "-5px" }}
                                    aria-label={`Delete: ${idx}`}
                                    title={`Delete: ${idx}`}
                                    icon={TrashIcon}
                                    variant="invisible"
                                    onClick={() => props.onFilesChanged(props.files.filter(x => x !== file))}
                                />
                            </ActionList.TrailingVisual>
                        </ActionList.LinkItem>
                    );
                })}
            </ActionList>
        </Box>
    );
};

export default ThucDonFiles;
